import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useUser } from "auth/useUser";
import { CaseTypeSelectDialog } from "pages/CreateMaterialCase/CaseTypeSelectDialog";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { AdminRoleSwitcher } from "../AdminRoleSwitcher";

type OpenMenu = "mbo" | "mav" | "mst" | null;

export function NavigationBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const mobileMenuOpen = Boolean(mobileMenuAnchor);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const canCreateCases = (user?.roles ?? []).some((r) =>
    ["Beredare", "Admin"].includes(r),
  );
  const canViewReports = (user?.roles ?? []).some((r) =>
    ["Chef", "Admin"].includes(r),
  );
  const isAdmin = (user?.roles ?? []).includes("Admin");

  const path = location.pathname;

  const isMyTasksActive = path === "/material/my-tasks" || path === "/";
  const isMboActive =
    path === "/kanban" ||
    path.startsWith("/cases") ||
    path.startsWith("/mbo") ||
    path === "/reports";
  const isMavActive =
    path.startsWith("/material/mav") ||
    path.startsWith("/material/kanban/mav") ||
    /\/material\/MAV\//.test(path);
  const isMstActive =
    path.startsWith("/material/mst") ||
    path.startsWith("/material/kanban/mst") ||
    /\/material\/MST\//.test(path);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setOpenMenu(null);
      setMenuAnchor(null);
    }, 150);
  }, [cancelClose]);

  const handleOpenMenu =
    (menu: OpenMenu) => (event: React.MouseEvent<HTMLElement>) => {
      cancelClose();
      setOpenMenu(menu);
      setMenuAnchor(event.currentTarget);
    };

  const handleCloseMenu = useCallback(() => {
    cancelClose();
    setOpenMenu(null);
    setMenuAnchor(null);
  }, [cancelClose]);

  const handleNavigate = (to: string) => {
    void navigate(to);
    handleCloseMenu();
    setMobileMenuAnchor(null);
  };

  return (
    <AppBar position="sticky" color="default" elevation={1}>
      <Toolbar>
        {isMobile && (
          <IconButton
            edge="start"
            aria-label="menu"
            onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Mobile hamburger menu */}
        {isMobile && (
          <Menu
            anchorEl={mobileMenuAnchor}
            open={mobileMenuOpen}
            onClose={() => setMobileMenuAnchor(null)}
          >
            <MenuItem
              selected={isMyTasksActive}
              onClick={() => handleNavigate("/material/my-tasks")}
            >
              {t("nav.myTasks")}
            </MenuItem>
            <Divider />
            <Typography
              variant="caption"
              sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
            >
              {t("nav.materialbrist")}
            </Typography>
            <MenuItem
              selected={path === "/mbo/overview"}
              onClick={() => handleNavigate("/mbo/overview")}
            >
              {t("nav.materialbrist.list")}
            </MenuItem>
            <MenuItem
              selected={path === "/kanban"}
              onClick={() => handleNavigate("/kanban")}
            >
              {t("nav.materialbrist.kanban")}
            </MenuItem>
            {canViewReports && (
              <MenuItem
                selected={path === "/reports"}
                onClick={() => handleNavigate("/reports")}
              >
                {t("nav.reports")}
              </MenuItem>
            )}
            <Divider />
            <Typography
              variant="caption"
              sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
            >
              {t("nav.materialavvikelse")}
            </Typography>
            <MenuItem
              selected={path === "/material/mav"}
              onClick={() => handleNavigate("/material/mav")}
            >
              {t("nav.materialavvikelse.list")}
            </MenuItem>
            <MenuItem
              selected={path === "/material/kanban/mav"}
              onClick={() => handleNavigate("/material/kanban/mav")}
            >
              {t("nav.materialavvikelse.kanban")}
            </MenuItem>
            <Divider />
            <Typography
              variant="caption"
              sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
            >
              {t("nav.materialsupport")}
            </Typography>
            <MenuItem
              selected={path === "/material/mst"}
              onClick={() => handleNavigate("/material/mst")}
            >
              {t("nav.materialsupport.list")}
            </MenuItem>
            <MenuItem
              selected={path === "/material/kanban/mst"}
              onClick={() => handleNavigate("/material/kanban/mst")}
            >
              {t("nav.materialsupport.kanban")}
            </MenuItem>
            {isAdmin && <Divider />}
            {isAdmin && (
              <MenuItem
                selected={path.startsWith("/admin")}
                onClick={() => handleNavigate("/admin/roles")}
              >
                {t("nav.admin")}
              </MenuItem>
            )}
            <Divider />
            <Box sx={{ px: 1, py: 0.5 }}>
              <AdminRoleSwitcher />
            </Box>
          </Menu>
        )}

        {/* App name */}
        <Typography
          variant="h4"
          sx={{ cursor: "pointer", mr: 4, fontWeight: 700, lineHeight: 1 }}
          onClick={() => void navigate("/")}
        >
          {t("nav.appName")}
        </Typography>

        {!isMobile ? (
          <>
            {/* Mina ärenden — standalone top-level button */}
            <Button
              color={isMyTasksActive ? "primary" : "inherit"}
              onClick={() => void navigate("/material/my-tasks")}
              sx={{ fontWeight: isMyTasksActive ? 700 : 400 }}
            >
              {t("nav.myTasks")}
            </Button>

            {/* Materialbrist dropdown */}
            <Box
              onMouseEnter={handleOpenMenu("mbo")}
              onMouseLeave={scheduleClose}
            >
              <Button
                color={isMboActive ? "primary" : "inherit"}
                onClick={handleOpenMenu("mbo")}
                endIcon={<ArrowDropDownIcon />}
                sx={{ fontWeight: isMboActive ? 700 : 400 }}
              >
                {t("nav.materialbrist")}
              </Button>
            </Box>

            {/* Materialavvikelse dropdown */}
            <Box
              onMouseEnter={handleOpenMenu("mav")}
              onMouseLeave={scheduleClose}
            >
              <Button
                color={isMavActive ? "primary" : "inherit"}
                onClick={handleOpenMenu("mav")}
                endIcon={<ArrowDropDownIcon />}
                sx={{ fontWeight: isMavActive ? 700 : 400 }}
              >
                {t("nav.materialavvikelse")}
              </Button>
            </Box>

            {/* Materialsupport Teknik dropdown */}
            <Box
              onMouseEnter={handleOpenMenu("mst")}
              onMouseLeave={scheduleClose}
            >
              <Button
                color={isMstActive ? "primary" : "inherit"}
                onClick={handleOpenMenu("mst")}
                endIcon={<ArrowDropDownIcon />}
                sx={{ fontWeight: isMstActive ? 700 : 400 }}
              >
                {t("nav.materialsupport")}
              </Button>
            </Box>

            {/* Popper-based dropdown — no Modal, no backdrop, no blink */}
            <Popper
              open={openMenu !== null}
              anchorEl={menuAnchor}
              placement="bottom-start"
              disablePortal
              style={{ zIndex: theme.zIndex.appBar + 1 }}
            >
              <ClickAwayListener onClickAway={handleCloseMenu}>
                <Paper
                  elevation={4}
                  onMouseEnter={cancelClose}
                  onMouseLeave={scheduleClose}
                  sx={{ minWidth: 180 }}
                >
                  <MenuList autoFocusItem={false}>
                    {openMenu === "mbo" && [
                      <MenuItem
                        key="mbo-list"
                        selected={path === "/mbo/overview"}
                        onClick={() => handleNavigate("/mbo/overview")}
                      >
                        {t("nav.materialbrist.list")}
                      </MenuItem>,
                      <MenuItem
                        key="mbo-kanban"
                        selected={path === "/kanban"}
                        onClick={() => handleNavigate("/kanban")}
                      >
                        {t("nav.materialbrist.kanban")}
                      </MenuItem>,
                      ...(canViewReports
                        ? [
                            <Divider key="mbo-divider" />,
                            <MenuItem
                              key="mbo-reports"
                              selected={path === "/reports"}
                              onClick={() => handleNavigate("/reports")}
                            >
                              {t("nav.reports")}
                            </MenuItem>,
                          ]
                        : []),
                    ]}
                    {openMenu === "mav" && [
                      <MenuItem
                        key="mav-list"
                        selected={path === "/material/mav"}
                        onClick={() => handleNavigate("/material/mav")}
                      >
                        {t("nav.materialavvikelse.list")}
                      </MenuItem>,
                      <MenuItem
                        key="mav-kanban"
                        selected={path === "/material/kanban/mav"}
                        onClick={() => handleNavigate("/material/kanban/mav")}
                      >
                        {t("nav.materialavvikelse.kanban")}
                      </MenuItem>,
                    ]}
                    {openMenu === "mst" && [
                      <MenuItem
                        key="mst-list"
                        selected={path === "/material/mst"}
                        onClick={() => handleNavigate("/material/mst")}
                      >
                        {t("nav.materialsupport.list")}
                      </MenuItem>,
                      <MenuItem
                        key="mst-kanban"
                        selected={path === "/material/kanban/mst"}
                        onClick={() => handleNavigate("/material/kanban/mst")}
                      >
                        {t("nav.materialsupport.kanban")}
                      </MenuItem>,
                    ]}
                  </MenuList>
                </Paper>
              </ClickAwayListener>
            </Popper>

            {/* Spacer — pushes Reports + Admin to the right */}
            <Box sx={{ flex: 1 }} />

            {isAdmin && (
              <Button
                color={path.startsWith("/admin") ? "primary" : "inherit"}
                onClick={() => void navigate("/admin/roles")}
                sx={{ fontWeight: path.startsWith("/admin") ? 700 : 400 }}
              >
                {t("nav.admin")}
              </Button>
            )}
          </>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}

        {canCreateCases &&
          (isMobile ? (
            <IconButton
              color="primary"
              aria-label={t("nav.createCase")}
              onClick={() => setDialogOpen(true)}
            >
              <AddIcon />
            </IconButton>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              {t("nav.createCase")}
            </Button>
          ))}
        {!isMobile && user && (
          <Box sx={{ ml: 2, textAlign: "right" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {user.displayName}
            </Typography>
          </Box>
        )}
        {!isMobile && <AdminRoleSwitcher />}
      </Toolbar>
      <CaseTypeSelectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </AppBar>
  );
}
